import React, { useRef } from 'react';
import { IconCaretDownNoRadius, IconGlobeFilled, IconHeadSet } from '../../../assets/icons';
import { UserState } from '../../../types/user.store.type';
import languages from '../../../assets/languages.json';
import { TranslationUtils, ValidationUtils, SmartPhoneFieldEnricher } from '../../../utils';
import { OptimizelyFeature } from '@optimizely/react-sdk';
import { Splits } from '../../../types/split.type';

interface RightNavMenuProps {
    user: UserState;
    localPhoneNumber?: string;
    tollFreePhoneNumber?: string;
    noLabelPhoneNumber?: string;
    onLanguageChange: (languageCode: string) => void;
    onCurrencyChange: (e: any) => void;
    onLogin: () => void;
    onLogout: () => void;
    updateLanguageInputWidth: () => void;
    updateCurrencyInputWidth: () => void;
}

interface LinkReference {
    currency: any;
    language: any;
    help: any;
    navbar: any;
}

export const RightNavBarMenu: React.FC<RightNavMenuProps> = (props) => {
    const {
        user,
        onLogin,
        onLogout,
        onLanguageChange,
        localPhoneNumber,
        tollFreePhoneNumber,
        noLabelPhoneNumber,
        updateLanguageInputWidth,
        updateCurrencyInputWidth,
    } = props;
    const vacasaUrl = ValidationUtils.validateAndGetEnvVar('REACT_APP_VACASA_URI');
    const linkReferences = {
        currency: useRef<any>(),
        language: useRef<any>(),
        help: useRef<any>(),
        navbar: useRef<any>(),
    };

    const isEnterOrSpaceKey = (key: number) => {
        return key === 13 || key === 32;
    };

    const handleLinkKeyDown = (e: any, name: keyof LinkReference) => {
        if (isEnterOrSpaceKey(e.keyCode)) {
            linkReferences[name].current.click();
        }
    };

    const handleOptionKeyDown = (e: any, callBack: (p?: any) => void, parameter?: any) => {
        if (isEnterOrSpaceKey(e.keyCode)) {
            e.preventDefault();
            callBack(parameter);
        }
    };

    const renderDropdownLink = (id: string, displayName: string, displayValue: string, className: string = '') => {
        return (
            <a tabIndex={0} id={id} className={`dropdown-item ${className}`} href={`tel:${displayValue}`}>
                {displayName !== '' ? `${TranslationUtils.get(displayName)} ${displayValue}` : displayValue}
            </a>
        );
    };

    const renderDropdownLinkUnsafePhone = (id: string, displayName: string, unsafePhone: string, className: string = '') => {
        const richTextfield = SmartPhoneFieldEnricher.from(unsafePhone);
        return (
            <a
                tabIndex={0}
                id={id}
                className={`dropdown-item ${className}`}
                href={richTextfield?.isValid() ? richTextfield.toString() : undefined}
            >
                {displayName !== '' ? `${TranslationUtils.get(displayName)} ${unsafePhone}` : unsafePhone}
            </a>
        );
    };

    updateLanguageInputWidth();
    updateCurrencyInputWidth();

    return (
        <div className="navbar-nav ml-auto fix-right-nav">
            <div
                tabIndex={0}
                className="nav-item-v2 nav-item dropdown d-flex mr-0 ml-0"
                onKeyDown={(e) => handleLinkKeyDown(e, 'language')}
            >
                <a
                    id="language-link"
                    className="nav-link-v2 dropdown-toggle d-flex mr-0"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    ref={linkReferences.language}
                >
                    <span id="display-language">
                        <IconGlobeFilled className="feather globe" />
                    </span>
                    <IconCaretDownNoRadius className="icon-caret-down-nav-v2" />
                </a>
                <div id="language-picker" className="dropdown-menu dropdown-menu-left mr-0 fix-language" aria-labelledby="language-link">
                    {languages.map((language, index) => {
                        return (
                            <a
                                tabIndex={0}
                                id={`option-${language.code}`}
                                className="dropdown-item nav-font"
                                key={index}
                                onClick={() => onLanguageChange(language.code)}
                                onKeyDown={(e) => handleOptionKeyDown(e, onLanguageChange, language.code)}
                            >
                                {language.name}
                            </a>
                        );
                    })}
                </div>
            </div>
            <div tabIndex={0} className="nav-item-v2 nav-item dropdown d-flex mr-0" onKeyDown={(e) => handleLinkKeyDown(e, 'help')}>
                <a
                    id="help-link"
                    className="nav-link-v2 dropdown-toggle d-flex mr-0"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    ref={linkReferences.help}
                >
                    <span>
                        <IconHeadSet className="mb-1 icon-head-set" />
                    </span>
                    <IconCaretDownNoRadius className="icon-caret-down-nav-v2" />
                </a>
                <div className="dropdown-menu dropdown-menu-left" aria-labelledby="help-link">
                    <a
                        id="navbar-support"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`${vacasaUrl}/support`}
                        className="dropdown-item nav-font"
                    >
                        {TranslationUtils.get('support_contact')}
                    </a>
                    {tollFreePhoneNumber && renderDropdownLink('navbar-tollfree-phone', 'toll_free', tollFreePhoneNumber, 'nav-font')}
                    {localPhoneNumber && renderDropdownLink('navbar-local-phone', 'local', localPhoneNumber, 'nav-font')}
                    {noLabelPhoneNumber && renderDropdownLinkUnsafePhone('navbar-local-phone', '', noLabelPhoneNumber, 'nav-font')}
                </div>
            </div>
            {user.user ? (
                <div
                    tabIndex={0}
                    className="nav-item-v2 nav-item dropdown d-flex mr-0 nav-font"
                    onKeyDown={(e) => handleLinkKeyDown(e, 'navbar')}
                >
                    <a
                        id="navbar-my-vacasa"
                        className="nav-link-v2 dropdown-toggle d-flex mr-0"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                        ref={linkReferences.navbar}
                    >
                        <span id="user-greeting" className={'account-name'}>
                            {TranslationUtils.get('hi')}
                            {user.user.name}
                        </span>
                        <IconCaretDownNoRadius className="icon-caret-down-nav-v2" />
                    </a>
                    <div className="my-vacasa-menu dropdown-menu dropdown-menu-left" aria-labelledby="navbar-my-vacasa">
                        <a tabIndex={0} target="_blank" rel="noopener noreferrer" href={`${vacasaUrl}/my-trips`} className="dropdown-item">
                            <span>{TranslationUtils.get('my_trips')}</span>
                        </a>
                        <div className="dropdown-divider" />
                        <a tabIndex={0} target="_blank" rel="noopener noreferrer" href={`${vacasaUrl}/favorites`} className="dropdown-item">
                            <span>{TranslationUtils.get('favorites')}</span>
                        </a>
                        <a
                            tabIndex={0}
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`${vacasaUrl}/my-profile`}
                            className="dropdown-item"
                        >
                            <span>{TranslationUtils.get('profile')}</span>
                        </a>
                        <div className="dropdown-divider" />
                        <a
                            tabIndex={0}
                            id="navbar-owner-login"
                            className="dropdown-item"
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://owners.vacasa.com/login"
                        >
                            <span>{TranslationUtils.get('owner_portal')}</span>
                        </a>
                        <div className="dropdown-divider" />
                        <a tabIndex={0} className="dropdown-item" onClick={onLogout}>
                            <span>{TranslationUtils.get('logout')}</span>
                        </a>
                    </div>
                </div>
            ) : (
                <div
                    tabIndex={0}
                    className="nav-item-v2 nav-item dropdown d-flex nav-font mr-0"
                    onKeyDown={(e) => handleLinkKeyDown(e, 'navbar')}
                >
                    <a
                        id="navbar-my-vacasa"
                        className="nav-link-v2 dropdown-toggle d-flex mr-0"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                        ref={linkReferences.navbar}
                    >
                        <span>{TranslationUtils.get('my_account')}</span>
                        <IconCaretDownNoRadius className="icon-caret-down-nav-v2" />
                    </a>
                    <div className="my-vacasa-menu dropdown-menu dropdown-menu-left" aria-labelledby="navbar-my-vacasa">
                        <a
                            tabIndex={0}
                            id="navbar-owner-login"
                            className="dropdown-item"
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://owners.vacasa.com/login"
                        >
                            <span>{TranslationUtils.get('owner_login')}</span>
                        </a>
                        <div className="dropdown-divider" />
                        <a
                            tabIndex={0}
                            id="navbar-login"
                            className="dropdown-item"
                            onClick={onLogin}
                            onKeyDown={(e) => handleOptionKeyDown(e, onLogin)}
                        >
                            <span>{TranslationUtils.get('login')}</span>
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};
